import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleAlert, Plus, X } from 'lucide-react';
import { isApiError } from '@missions/api-client';
import type { Priority } from '@missions/types';
import { Button, DatePicker, Select, useDialogs, useToast } from '@missions/ui';
import { Layer } from '../../components/Layer';
import { api } from '../../lib/client';
import { useApi } from '../../lib/hooks';
import { useT } from '../../lib/session';
import { useShell, type NewMissionDefaults } from '../../layout/shell-context';
import { priorityOptions, statusOptions, useMemberOptions, useMissionInvalidate, useProjects } from './data';

interface Draft {
  title: string;
  projectId: string | null;
  statusId: string | null;
  assigneeIds: string[];
  dueAt: string | null;
  priority: Priority;
  eta: string;
  description: string;
}
const EMPTY: Draft = { title: '', projectId: null, statusId: null, assigneeIds: [], dueAt: null, priority: 'normal', eta: '', description: '' };

/** "Create mission" modal (mockups/app.html #newModal) with unsaved-changes guard. */
export function CreateMissionModal({ defaults, onClose }: { defaults: NewMissionDefaults | null; onClose: () => void }) {
  const open = defaults !== null;
  const t = useT();
  const shell = useShell();
  const toast = useToast();
  const dialogs = useDialogs();
  const nav = useNavigate();
  const refresh = useMissionInvalidate();
  const { flat } = useProjects(shell.workspaceId);
  const members = useMemberOptions();
  const [d, setD] = useState<Draft>(EMPTY);
  const [another, setAnother] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const project = useApi('projects.get', { params: { id: d.projectId ?? '' } }, { enabled: !!d.projectId });
  const statuses = useMemo(() => project.data?.statuses ?? [], [project.data]);

  useEffect(() => {
    if (!open) return;
    const pid = defaults?.projectId ?? flat[0]?.id ?? null;
    setD({ ...EMPTY, projectId: pid, statusId: defaults?.statusId ?? null, dueAt: defaults?.dueAt ?? null });
    setErr(null);
    setTimeout(() => nameRef.current?.focus(), 60);
    // reset only when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!d.statusId && statuses.length) {
      const first = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder).find((s) => !s.isClosed);
      if (first) setD((x) => ({ ...x, statusId: first.id }));
    }
  }, [statuses, d.statusId]);

  const dirty = useMemo(() => d.title.trim() !== '' || d.description.trim() !== '' || d.assigneeIds.length > 0 || !!d.dueAt || d.eta !== '', [d]);
  const nameErr = (v: string) => (!v ? `Give the ${t.item} a name` : v.length < 3 ? 'Use at least 3 characters' : null);

  const requestClose = async () => {
    if (!dirty) return onClose();
    const r = await dialogs.choice({
      title: 'You have unsaved changes',
      tone: 'warning',
      message: `Your new ${t.item} hasn’t been created yet. Discard it?`,
      actions: [
        { label: 'Keep editing', value: 'stay', cancel: true },
        { label: 'Discard', value: 'discard', tone: '' },
      ],
    });
    if (r === 'discard') onClose();
  };

  const submit = async () => {
    const e = nameErr(d.title.trim());
    setErr(e);
    if (e) {
      nameRef.current?.focus();
      return;
    }
    if (!d.projectId) {
      toast('Choose a project first', { type: 'error' });
      return;
    }
    setBusy(true);
    try {
      const m = await api.call('missions.create', {
        body: {
          projectId: d.projectId,
          title: d.title.trim(),
          description: d.description.trim() || undefined,
          statusId: d.statusId ?? undefined,
          priority: d.priority,
          dueAt: d.dueAt,
          etaMinutes: d.eta ? Math.round(parseFloat(d.eta) * 60) : null,
          assigneeIds: d.assigneeIds,
        },
      });
      await refresh();
      toast(`${t.Item} ${m.key} created`, { action: { label: 'Open', onClick: () => nav(`/p/${m.projectId}?m=${m.id}`) } });
      if (another) {
        setD((x) => ({ ...x, title: '', description: '', eta: '' }));
        nameRef.current?.focus();
      } else onClose();
    } catch (ex) {
      if (isApiError(ex) && ex.fieldErrors['title']) setErr(ex.fieldErrors['title']);
      else toast(isApiError(ex) ? ex.message : `Couldn’t create the ${t.item}`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layer open={open} onRequestClose={() => void requestClose()} className="modal" labelledBy="newT">
      <form
        noValidate
        style={{ display: 'contents' }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="m-h">
          <h2 id="newT">{t.createItem}</h2>
          <button type="button" className="ib" onClick={() => void requestClose()} aria-label="Close"><X /></button>
        </div>
        <div className="m-b">
          <label className="lbl" htmlFor="nName">{t.Item} name <span className="req">*</span></label>
          <input
            ref={nameRef}
            className="ui-input"
            id="nName"
            autoComplete="off"
            placeholder="e.g. Homepage hero copy — 2 options"
            maxLength={140}
            value={d.title}
            aria-invalid={!!err}
            onChange={(e) => {
              setD({ ...d, title: e.target.value });
              if (err) setErr(nameErr(e.target.value.trim()));
            }}
          />
          {err && <div className="ferr"><CircleAlert /><span>{err}</span></div>}
          <div className="fgrid">
            <div>
              <span className="lbl">Project</span>
              <Select
                value={d.projectId}
                onChange={(v) => setD({ ...d, projectId: v, statusId: null })}
                options={flat.map((p) => ({ value: p.id, label: p.name, color: p.color, group: p.folder ?? undefined }))}
                searchable
                aria-label="Project"
                title="Project"
              />
            </div>
            <div>
              <span className="lbl">Status</span>
              <Select value={d.statusId} onChange={(v) => setD({ ...d, statusId: v })} options={statusOptions(statuses)} aria-label="Status" title="Status" placeholder="Status" />
            </div>
            <div>
              <span className="lbl">Assignees</span>
              <Select multiple value={d.assigneeIds} onChange={(v) => setD({ ...d, assigneeIds: v })} options={members.options} searchable placeholder="Unassigned" aria-label="Assignees" title="Assignees" />
            </div>
            <div>
              <span className="lbl">Due date</span>
              <DatePicker value={d.dueAt} onChange={(v) => setD({ ...d, dueAt: v })} due placeholder="No due date" aria-label="Due date" />
            </div>
            <div>
              <span className="lbl">Priority</span>
              <Select value={d.priority} onChange={(v) => v && setD({ ...d, priority: v })} options={priorityOptions()} aria-label="Priority" title="Priority" />
            </div>
            <div>
              <label className="lbl" htmlFor="nEta">ETA (hours)</label>
              <input className="ui-input" id="nEta" inputMode="decimal" placeholder="e.g. 4" value={d.eta} onChange={(e) => setD({ ...d, eta: e.target.value.replace(/[^\d.]/g, '') })} />
            </div>
          </div>
          <label className="lbl" htmlFor="nDesc">Description</label>
          <textarea className="ui-textarea" id="nDesc" placeholder="Details, links, acceptance notes…" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
        </div>
        <div className="m-f">
          <label className="swl">
            <input type="checkbox" className="sr" checked={another} onChange={(e) => setAnother(e.target.checked)} />
            <span className="sw" />
            Create another
          </label>
          <span className="sp" />
          <Button type="button" onClick={() => void requestClose()}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy}><Plus />{t.createItem}</Button>
        </div>
      </form>
    </Layer>
  );
}
